import { useEffect, useState } from 'react';
import { fetchUsers } from '../services/userService';
import type { UserListQuery } from '../services/userService';
import type { UserPage } from '../services/types';

/** Zustand der geladenen Benutzerliste. */
export interface UsersState {
  /** Geladene Seite oder null (laedt noch bzw. Fehler). */
  readonly data: UserPage | null;
  /** true, waehrend ein Request laeuft. */
  readonly loading: boolean;
  /** Deutsche Fehlermeldung oder null. */
  readonly error: string | null;
}

/** Fehlermeldung fuer fehlgeschlagene Listen-Requests. */
const LOAD_ERROR_MESSAGE = 'Die Benutzerliste konnte nicht geladen werden.';

/** Geladenes Ergebnis samt Schluessel der zugehoerigen Abfrage. */
interface LoadedUsers {
  readonly key: string;
  readonly data: UserPage | null;
  readonly error: string | null;
}

/** Eindeutiger Schluessel einer Abfragekombination. */
function buildRequestKey(query: UserListQuery, search: string): string {
  return [search, query.page, query.size, query.sortField, query.sortDirection].join('|');
}

/**
 * Laedt eine Seite der Benutzerliste und laedt bei jeder Aenderung von
 * Suche, Seite, Seitengroesse oder Sortierung neu (kein Caching,
 * Projektstandard).
 *
 * Stale-Guard wie in useTimeSeries: Zum Ergebnis wird der Schluessel der
 * Abfrage mitgefuehrt. Passt er nicht zur aktuellen Abfrage (Request noch
 * unterwegs), meldet der Hook `loading` und liefert KEINE Daten — sonst
 * stuenden kurzzeitig die Treffer der vorherigen Suche unter der neuen
 * Seitenangabe. Zusaetzlich wird das geladene Ergebnis beim START jedes
 * Requests verworfen: Sonst zeigte ein Wechsel A -> B -> A (zurueck auf
 * eine schon einmal geladene Abfrage) sofort wieder die alten Daten,
 * obwohl der zugehoerige Request noch laeuft (kein Caching,
 * Projektstandard).
 *
 * Der Suchbegriff wird EINMAL getrimmt und sowohl fuer den Schluessel als
 * auch fuer den Request verwendet — sonst wuerde eine reine
 * Leerzeichen-Aenderung den Schluessel drehen, ohne den Request zu
 * aendern.
 *
 * Fehler werden bewusst still behandelt (nur Fehlerzustand, KEIN
 * console.error): Die Liste rendert den Fehlerzustand; zusaetzliche
 * Console-Errors wuerden die E2E-Error-Collector-Fixture ausloesen.
 */
export function useUsers(query: UserListQuery): UsersState {
  const search = query.search.trim();
  const requestKey = buildRequestKey(query, search);
  const [loaded, setLoaded] = useState<LoadedUsers | null>(null);
  const { page, size, sortField, sortDirection } = query;

  useEffect(() => {
    const controller = new AbortController();
    setLoaded(null);
    fetchUsers({ search, page, size, sortField, sortDirection }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          // Abbruch durch Parameterwechsel oder Unmount: Ergebnis verwerfen
          return;
        }
        setLoaded({ key: requestKey, data: response, error: null });
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        setLoaded({ key: requestKey, data: null, error: LOAD_ERROR_MESSAGE });
      });
    return () => {
      controller.abort();
    };
  }, [search, page, size, sortField, sortDirection, requestKey]);

  const isCurrent = loaded !== null && loaded.key === requestKey;
  return {
    data: isCurrent ? loaded.data : null,
    loading: !isCurrent,
    error: isCurrent ? loaded.error : null,
  };
}

export default useUsers;
